import { Grid, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { FinanceApplicationStatus } from '@/Modules/CustomersModule/shared/enums/customer-finance-application.enum';
import { CustomerSourceOptions } from '@/Modules/CustomersModule/shared/enums/customer-personal-data.enums';
import { getModuleName } from '@/Modules/ReportsModule/FinanceApplicationReport/shared/services/finance-applications-reports.service';
import useFinanceApplicationReportStore from '@/Modules/ReportsModule/FinanceApplicationReport/shared/store/finance-applications-reports.store';
import {
  initialFinanceApplicationsFiltersValues,
  validateFinanceApplicationReportFiltersForm
} from '@/Modules/ReportsModule/FinanceApplicationReport/shared/utils/helpers/filters-validation';
import { mapValuesToQuery } from '@/Modules/ReportsModule/FinanceApplicationReport/shared/utils/helpers/utils';
import Alert from '@/shared/components/core-ui/alert/alert.component';
import Button from '@/shared/components/core-ui/button/button.component';
import FormSectionTitle from '@/shared/components/core-ui/form/form-section-title/form-section-title.component';
import FormDatePicker from '@/shared/components/group-ui/form-controls/form-date-picker/form-date-picker.component';
import FormMultiSelect from '@/shared/components/group-ui/form-controls/select/multi-select/multi-select.component';
import FormSelect from '@/shared/components/group-ui/form-controls/select/select/select.component';
import FormSelectWithSearch from '@/shared/components/group-ui/form-controls/select/select-with-search/select-with-search.component';
import PageHeader from '@/shared/components/group-ui/page-header/page-header.component';
import { APP_ROUTES } from '@/shared/config/routes-config';
import { useAlert } from '@/shared/hooks/alert/useAlert';
import { useForm } from '@/shared/hooks/form/useForm';
import { useLocalization } from '@/shared/hooks/useLocalization';
import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import { getAllAgents } from '@/shared/services/data/finance-applications/finance-applications.data';
import { convertEnumsToIDLabelArr } from '@/shared/services/helpers/format-enums/id-label-format/convert-enum-to-id-label';
import { FinanceApplicationReportUIFilters } from '@/shared/ui-models/finance-application-report.ui-model';

function FinanceApplicationReportFilterContainer(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, isRTL } = useLocalization();

  const { isOpenAlert, setIsOpenAlert, alertText, showAlert } = useAlert();

  const {
    searchLoading,
    dataLoading,
    financeMerchantSearchQuery,
    financeMerchantSelectOptions,
    carofiAgentsSelectOptions,
    query,
    actions: {
      setSearchLoading,
      setFinanceMerchantSearchQuery,
      getFinanceMerchants,
      setCarofiAgentsSelectOptions,
      setQuery
    }
  } = useFinanceApplicationReportStore();

  const { values, setValues, errors, setErrors, handleInputChange } =
    useForm<FinanceApplicationReportUIFilters>(
      initialFinanceApplicationsFiltersValues,
      false,
      (fieldValues, errors, setErrors) => validateFinanceApplicationReportFiltersForm(fieldValues, errors, setErrors, t)
    );

  const customerSourceItems = useMemo(() => {
    return convertEnumsToIDLabelArr({ ...CustomerSourceOptions });
  }, []);

  useEffect(() => {
    getAllAgents(getModuleName()).then((res) => {
      if (isErrorInfo(res)) {
        showAlert(res.errorMessage);
      } else {
        const carofiAgentsSelectOptions = res.map((user) => ({
          id: user.id ?? '',
          label: user.name ?? ''
        }));
        setCarofiAgentsSelectOptions(carofiAgentsSelectOptions);

        setValues(
          mapValuesToQuery(query.query ?? {}, {
            statusSelectOptions,
            carofiAgentsSelectOptions,
            financeMerchantSelectOptions,
            customerSourceItems
          }) as FinanceApplicationReportUIFilters
        );
      }
    });
  }, []);

  useEffect(() => {
    getFinanceMerchants({ merchantType: 'finance', showAlert: showAlert });
  }, [financeMerchantSearchQuery]);

  useEffect(() => {
    if (!values.startDate && !values.endDate) {
      return;
    }

    validateFinanceApplicationReportFiltersForm(values, errors, setErrors, t);
  }, [values, t]);

  const statusSelectOptions = useMemo(() => {
    return convertEnumsToIDLabelArr({ ...FinanceApplicationStatus });
  }, []);

  const handleGenerateReport = (): void => {
    const { valid } = validateFinanceApplicationReportFiltersForm(values, errors, setErrors, t);
    if (!valid) return;

    setQuery(values);

    navigate(
      (APP_ROUTES.REPORTS_MODULE.CHILDREN as RouteConfig).FINANCE_APPLICATIONS_REPORT.FULL_PATH
    );
  };
  return (
    <>
      <PageHeader
        data={{
          title: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.PAGE_HEADERS.TITLE'),
          description: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.PAGE_HEADERS.DESCRIPTION')
        }}
        components={{
          actionsList: []
        }}
      />

      <Grid container>
        <Grid
          container
          sx={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            p: 3,
            m: '2rem 3rem 0',
            [theme.breakpoints.down('sm')]: {
              textAlign: 'center',
              p: 1,
              m: '1rem 1.5rem 0'
            }
          }}
        >
          <Grid item xs={12}>
            <FormSectionTitle title={t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.GENERATE_REPORT')} />
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormDatePicker
                data={{
                  name: 'startDate',
                  label: `${t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.START_DATE')}*`,
                  value: values.startDate,
                  error: errors.startDate
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormDatePicker
                data={{
                  name: 'endDate',
                  label: `${t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.END_DATE')}*`,
                  value: values.endDate,
                  error: errors.endDate
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormMultiSelect
                data={{
                  name: 'status',
                  label: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.STATUS'),
                  placeholder: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.STATUS_PLACEHOLDER'),
                  value: values.status ?? [],
                  error: errors.status,
                  selectOptions: statusSelectOptions,
                  noResultsFoundText: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.TABLE.NO_APPLICATIONS_FOUND')
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormMultiSelect
                data={{
                  name: 'agent',
                  label: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.AGENT'),
                  placeholder: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.AGENT_PLACEHOLDER'),
                  value: values.agent ?? [],
                  error: errors.agent,
                  selectOptions: carofiAgentsSelectOptions,
                  noResultsFoundText: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.TABLE.NO_AGENT_FOUND')
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={4}>
              <FormSelectWithSearch
                data={{
                  name: 'financialMerchantId',
                  label: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.FINANCIAL_MERCHANT'),
                  placeholder: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.FINANCIAL_MERCHANT_PLACEHOLDER'),
                  searchTextPlaceholder: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.FINANCIAL_MERCHANT_SEARCH'),
                  value: values.financialMerchantId ?? null,
                  error: errors.financialMerchantId,
                  selectOptions: financeMerchantSelectOptions,
                  searchLoading: searchLoading,
                  dataLoading: dataLoading,
                  noResultsFoundText: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.TABLE.NO_MERCHANTS_FOUND')
                }}
                eventHandlers={{
                  handleInputChange,
                  setSearchLoading: setSearchLoading,
                  setSearchQuery: setFinanceMerchantSearchQuery
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormMultiSelect
                data={{
                  name: 'source',
                  label: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.CUSTOMER_SOURCE'),
                  placeholder: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.CUSTOMER_SOURCE_PLACEHOLDER'),
                  value: values.source ?? [],
                  error: errors.source,
                  selectOptions: customerSourceItems,
                  noResultsFoundText: t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.TABLE.NO_SOURCE_FOUND')
                }}
                eventHandlers={{ handleInputChange }}
              />
            </Grid>
          </Grid>
          <Button
            config={{}}
            eventHandlers={{
              handleClick: handleGenerateReport
            }}
          >
            {t('REPORTS_MODULE.FINANCE_APPLICATIONS_REPORT.FORMS.GENERATE_REPORT')}
          </Button>
        </Grid>
      </Grid>

      <Alert
        data={{ text: alertText }}
        config={{ isOpen: isOpenAlert }}
        eventHandlers={{ setOpen: setIsOpenAlert }}
      />
    </>
  );
}

export default FinanceApplicationReportFilterContainer;
